import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Timer, Eraser } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const IDLE = [1, 2, 4, 8, 12, 24, 48, 72].map((h) => ({ v: h, label: h === 1 ? "1 hour" : h < 24 ? `${h} hours` : `${h / 24} day${h > 24 ? "s" : ""}` }));
const RETENTION = [{ v: 0, label: "Keep forever" }, { v: 6, label: "6 months" }, { v: 12, label: "12 months" }, { v: 24, label: "24 months" }, { v: 36, label: "3 years" }, { v: 60, label: "5 years" }];

// Dealer-level security knobs: idle sign-out for the team, and how long shopper contact details stay on leads
export function SecuritySettingsCard() {
  const [dealer, setDealer] = useState(null);
  useEffect(() => { api.get("/dealer").then(({ data }) => setDealer(data)).catch(() => setDealer(null)); }, []);
  if (!dealer) return null;

  const set = async (key, v, msg) => {
    try {
      const { data } = await api.put("/dealer", { [key]: Number(v) });
      setDealer(data);
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const Row = ({ icon: Icon, title, desc, value, options, testId, onChange }) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1 min-w-0">
      <div className="flex-1 min-w-0">
        <div className="font-semibold flex items-center gap-2 text-sm"><Icon className="h-4 w-4 text-[#0A8CF2]" /> {title}</div>
        <p className="text-xs text-slate-400 mt-0.5">{desc}</p>
      </div>
      <Select value={String(value)} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="w-40 bg-slate-950 border-slate-800 text-slate-200"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-[#12141C] border-[#2A2E3D] text-slate-200">
          {options.map((o) => <SelectItem key={o.v} value={String(o.v)}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="rounded-xl border border-[#2A2E3D] bg-[#12141C] px-5 py-4 mb-4 flex flex-col lg:flex-row gap-5 lg:gap-10" data-testid="security-settings">
      <Row icon={Timer} title="Idle sign-out" testId="session-idle-select" value={dealer.session_idle_hours ?? 12} options={IDLE}
        desc="Team members are signed out after this much inactivity. Vinconex platform accounts are always 2 hours."
        onChange={(v) => set("session_idle_hours", v, `Idle sign-out set to ${IDLE.find((o) => o.v === Number(v))?.label}`)} />
      <Row icon={Eraser} title="Shopper data retention" testId="pii-retention-select" value={dealer.pii_retention_months ?? 24} options={RETENTION}
        desc="Names, phones, emails and comments are scrubbed from leads older than this (the lead itself and its CRM copy stay). Nightly."
        onChange={(v) => set("pii_retention_months", v, Number(v) === 0 ? "Shopper details kept indefinitely" : `Shopper details purged after ${RETENTION.find((o) => o.v === Number(v))?.label}`)} />
    </div>
  );
}
